import { useInfiniteQuery } from '@tanstack/react-query';
import { apiGetWithMeta } from '../lib/api.js';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { DataRow, DataTable, TableCell, TableHead } from '../ui/DataTable.js';
import { EmptyState } from '../shell/EmptyState.js';
import { formatRelativeTime } from '../lib/format.js';
import { AuditDiff } from './AuditDiff.js';

type AuditEntry = {
  id: number;
  action: string;
  entityType: string;
  entityId: number | null;
  before: unknown;
  after: unknown;
  at: string;
  userEmail: string | null;
};

type AuditPage = { entries: AuditEntry[] };
type AuditPageWithCursor = AuditPage & { nextCursor: number | null };

export function AuditScreen() {
  const audit = useInfiniteQuery({
    queryKey: ['audit'],
    initialPageParam: null as number | null,
    queryFn: async ({ pageParam }) => {
      const query = pageParam === null ? '' : `&cursor=${pageParam}`;
      // The cursor rides in the envelope's meta, not in the resource itself.
      const page = await apiGetWithMeta<AuditPage>(`/api/audit?limit=50${query}`);
      const cursor = page.meta.nextCursor;
      const next: AuditPageWithCursor = {
        ...page.data,
        nextCursor: typeof cursor === 'number' ? cursor : null,
      };
      return next;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const entries = audit.data?.pages.flatMap((page) => page.entries) ?? [];

  return (
    <section aria-labelledby="audit-heading" className="flex flex-col gap-(--space-5)">
      <header>
        <p className="text-[length:var(--text-micro)] uppercase tracking-[0.18em] text-ink-subtle">
          History
        </p>
        <h1 id="audit-heading" className="mt-(--space-1) text-[length:var(--text-display)]">
          Audit log
        </h1>
        <p className="mt-(--space-2) max-w-[64ch] text-ink-muted">
          Every configuration change, newest first, with who made it and what it changed.
        </p>
      </header>

      {audit.isError && <Callout tone="danger">Could not load the audit log.</Callout>}

      {entries.length === 0 && !audit.isLoading && !audit.isError && (
        <EmptyState
          title="Nothing recorded yet"
          description="Every configuration change will appear here, with who made it and what changed."
        />
      )}

      {entries.length > 0 && (
        <>
          <DataTable
            caption="Configuration changes, newest first"
            head={
              <>
                <TableHead scope="col">When</TableHead>
                <TableHead scope="col">Who</TableHead>
                <TableHead scope="col">Action</TableHead>
                <TableHead scope="col">Change</TableHead>
              </>
            }
          >
            {entries.map((entry) => (
              <DataRow key={entry.id}>
                <TableCell className="align-top py-(--space-3) text-[length:var(--text-small)] text-ink-muted">
                  <time dateTime={entry.at}>{formatRelativeTime(entry.at)}</time>
                </TableCell>
                <TableCell className="align-top py-(--space-3) text-[length:var(--text-small)] font-bold">
                  {entry.userEmail ?? 'system'}
                </TableCell>
                <TableCell className="align-top py-(--space-3) font-mono text-[length:var(--text-small)]">
                  {entry.action}
                </TableCell>
                <TableCell className="align-top py-(--space-3)">
                  <AuditDiff before={entry.before} after={entry.after} />
                </TableCell>
              </DataRow>
            ))}
          </DataTable>

          {audit.hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                onClick={() => void audit.fetchNextPage()}
                disabled={audit.isFetchingNextPage}
              >
                {audit.isFetchingNextPage ? 'Loading…' : 'Load older changes'}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
